import {useCallback} from "react"
import {useQuery} from "react-query"
import useAuth from "auth/useAuth"
import {setLocalItem} from "utils/utils"

export default function useRescuers() {
    
    const {auth, API} = useAuth()
    const localStorageKey = 'rescuers'
        
    // Cache may hold a non-array (seen in production: an empty response cached as "") — discard it
    const preload = () => {
        try {
            const data = JSON.parse(localStorage.getItem(localStorageKey))
            if (Array.isArray(data)) return data
        } catch { /* corrupt cache */ }
        localStorage.removeItem(localStorageKey)
        return []
    }

    const fetched = (data) => {
        setLocalItem(localStorageKey, data)
    }

    const fetchRescuers = () => auth(API.rescuersAllGet).then(response => {
        if (!Array.isArray(response.data))
            throw new Error(response.data?.errorMessage || "Neispravan odgovor servera")
        return response.data
    })

    const query = useQuery(
        ["rescuers"],
        fetchRescuers,
        {initialData: preload, onSuccess: fetched}
    )
    
    const getUser = (username) => {
        if (!query.data) return null
        for (const user of query.data)
            if (user.username === username)
                return user
        return null
    }
    
    const getTags = (username) => {
        const user = getUser(username)
        return user?.tags
    }
    
    const hasTag = (username, tag) => {
        const tags = getTags(username)
        return tags?.includes(tag)
    }

    const formatName = (user) => {
        if (!user) return ''
        return user.given_name + ' ' + user.family_name
    }
    
    const byUsername = (a, b) => {
        const ua = a.username.padStart(5, '0')
        const ub = b.username.padStart(5, '0')
        if (ua < ub) return -1
        if (ua > ub) return 1
        return 0
    }
    
    
    const filterUsers = (query, users) => {
        if (!query || query.length === 0 || typeof query !== "string" || !Array.isArray(users)) return []

        const tokens = []
        const regex = /"([^"]+)"|(\S+)/g
        let match
        while ((match = regex.exec(query.toLowerCase())) !== null) {
            tokens.push(match[1] || match[2])
        }

        const levelOrder = ['pripravnik', 'spasilac', 'vođa_tima', 'instruktor']

        const getLevelRank = (level) => {
            const index = levelOrder.indexOf(level)
            return index === -1 ? -1 : index
        }

        const matchLevel = (partial) => {
            return levelOrder.find(level => level.startsWith(partial))
        }

        return tokens.reduce((results, token) => {
            if (token.startsWith("#") || token.startsWith("tag:")) {
                const tag = token.startsWith("#") ? token.substring(1) : token.substring(4)
                return results.filter(user => hasTag(user.username, tag))
            }

    		if (["has:licence", "has:license"].includes(token)) {
                return results.filter(user => user.coef > 0)
            }

            if ("has:ski" === token) {
                return results.filter(user => ["A", "A-", "B+"].includes(user.ski))
            }

            // Check for has:domain_level syntax (e.g., has:ski_level)
            const hasLevelMatch = token.match(/^has:(ski|vu|opu)_level$/)
            if (hasLevelMatch) {
                const domain = hasLevelMatch[1]
                return results.filter(user => user.levels?.[domain])
            }

            // Check for domain_level comparison syntax (e.g., ski_level:spasilac, ski_level>=spasilac)
            const levelMatch = token.match(/^(ski|vu|opu)_level([><]=?|:)(.+)$/)
            if (levelMatch) {
                const [, domain, operator, value] = levelMatch
                const matchedLevel = matchLevel(value)

                if (!matchedLevel) {
                    // Invalid level name, no results
                    return []
                }

                const targetRank = getLevelRank(matchedLevel)

                return results.filter(user => {
                    const userLevel = user.levels?.[domain]
                    if (!userLevel) return false

                    const userRank = getLevelRank(userLevel)
                    if (userRank === -1) return false

                    switch (operator) {
                        case ":": return userRank === targetRank
                        case ">": return userRank > targetRank
                        case "<": return userRank < targetRank
                        case ">=": return userRank >= targetRank
                        case "<=": return userRank <= targetRank
                        default: return false
                    }
                })
            }

            const numMatch = token.match(/^(\w+)([><]=?|:)(\d+(\.\d+)?)$/)
            if (numMatch) {
                const [, field, operator, value] = numMatch
                const numValue = parseFloat(value)
                if (["coef", "rank"].includes(field)) {
                    return results.filter(user => {
                        if (user[field] === undefined || user[field] == null) return false
                        switch (operator) {
                            case ":": return user[field] == numValue
                            case ">": return user[field] > numValue
                            case "<": return user[field] < numValue
                            case ">=": return user[field] >= numValue
                            case "<=": return user[field] <= numValue
                        }
                    })
                }
                if (["admission_year", "gss_id", "username"].includes(field)) {
                    return results.filter(user => {
                        if (user[field] === undefined || isNaN(parseInt(user[field]))) return false
                        const userValue = parseInt(user[field])
                        switch (operator) {
                            case ":": return userValue == numValue
                            case ">": return userValue > numValue
                            case "<": return userValue < numValue
                            case ">=": return userValue >= numValue
                            case "<=": return userValue <= numValue
                        }
                    })
                }
            }

            const fieldMatch = token.match(/^([a-z_]+):(.+)$/)
            if (fieldMatch) {
                const [, field, value] = fieldMatch
                if (field === "nickname") {
                    return results.filter(user =>
                        user.nickname?.toLowerCase() === value ||
                        user.nicknames?.toLowerCase().split(",").includes(value)
                    )
                }
                return results.filter(user => user[field]?.toString().toLowerCase() === value)
            }

            return results.filter(user =>
                ["username", "given_name", "family_name", "nickname", "nicknames", "gss_id", "city", "station", "phone_number"].some(
                    field => user[field]?.toString().toLowerCase().includes(token)
                )
            )
        }, users)
    }

    const filter = useCallback(q => {
        return filterUsers(q, query.data)
    }, [query.data])

    return {...query, getUser, getTags, hasTag, formatName, byUsername, filter}

}